(() => {
  'use strict';
  const abortError = () => new DOMException('Aborted', 'AbortError');
  const hostingQuotaMessage = 'Le service de discussion est temporairement indisponible : le quota d’hébergement est atteint. Votre texte est conservé ; vous pourrez réessayer sans le ressaisir.';
  async function productFragment(url, signal) {
    if (signal.aborted) throw signal.reason || abortError();
    const controller = new AbortController();
    const abort = () => controller.abort(signal.reason || abortError());
    signal.addEventListener('abort', abort, { once: true });
    let cancelled;
    const cancellation = new Promise((resolve, reject) => {
      cancelled = () => reject(controller.signal.reason || abortError());
      controller.signal.addEventListener('abort', cancelled, { once: true });
    });
    const timer = setTimeout(() => controller.abort(new DOMException('Product timed out', 'TimeoutError')), 8000);
    try {
      return await Promise.race([
        (async () => {
          const response = await fetch(url, { method: 'GET', credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: controller.signal });
          if (!response.ok) throw new Error('Product unavailable');
          return response.text();
        })(),
        cancellation
      ]);
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      controller.signal.removeEventListener('abort', cancelled);
    }
  }
  if (!customElements.get('olea-assistant')) {
    customElements.define('olea-assistant', class extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.events = new AbortController();
        const signal = this.events.signal;
        document.addEventListener('olea:header-panel-open', event => { if (event.detail.source !== this) this.close(false, true); }, { signal });
        document.addEventListener('olea:header-panel-close', event => { this.close(event.detail.restoreFocus); }, { signal });
        this.dialog = this.querySelector('dialog');
        this.form = this.querySelector('form');
        this.input = this.querySelector('textarea');
        this.feed = this.querySelector('[data-assistant-feed]');
        this.viewport = this.querySelector('.assistant-dialog__conversation');
        this.welcome = this.querySelector('[data-assistant-welcome]');
        this.status = this.querySelector('[data-assistant-status]');
        this.banReference = this.querySelector('[data-assistant-ban-reference]');
        this.referenceLabel = this.querySelector('[data-assistant-reference-label]');
        this.referenceCopyButton = this.querySelector('[data-assistant-copy-reference]');
        this.referenceStatus = this.querySelector('[data-assistant-reference-status]');
        const identityStatus = this.querySelector('.assistant-dialog__header .assistant-identity p');
        this.identityStatusText = Array.from(identityStatus?.childNodes || []).find(node => node.nodeType === 3 && node.textContent.trim());
        this.identityDefaultStatus = this.identityStatusText?.textContent;
        this.identityStatusDot = identityStatus?.querySelector('.online-dot');
        this.identityDefaultDotHidden = this.identityStatusDot?.hidden;
        this.identityTitle = this.querySelector('.assistant-dialog__header .assistant-identity h2');
        this.identityDefaultTitle = Array.from(this.identityTitle?.childNodes || []).map(node => node.cloneNode(true));
        this.identityAvatar = this.querySelector('.assistant-dialog__header .assistant-identity > .assistant-avatar');
        this.identityDefaultAvatar = Array.from(this.identityAvatar?.childNodes || []).map(node => node.cloneNode(true));
        this.cancelButton = this.querySelector('[data-assistant-stop]');
        this.submitButton = this.querySelector('[data-assistant-send]');
        this.ideas = this.querySelector('.assistant-ideas');
        this.ideasButton = this.querySelector('[data-assistant-ideas]');
        this.micButton = this.querySelector('[data-assistant-mic]');
        this.Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
        this.micButton.hidden = false;
        this.history = [];
        this.chatNodes = [];
        this.debugEnabled = this.debugEnabled === true;
        this.supportSeen = new Set();
        this.supportState = null;
        this.supportConnecting = false;
        this.serviceIssue = null;
        this.version = 0;
        this.strings = JSON.parse(this.querySelector('[data-assistant-strings]').textContent);
        this.repaintDebug();
        this.initializeMascot();
        this.setAttribute('dir', String(this.dataset.language || document.documentElement.lang).replace(/_/g, '-').split('-')[0].toLowerCase() === 'ar' ? 'rtl' : 'ltr');
        this.localizeInterface();
        this.identityDefaultStatus = this.identityStatusText?.textContent;
        this.identityDefaultTitle = Array.from(this.identityTitle?.childNodes || []).map(node => node.cloneNode(true));
        if (!this.Speech) this.micButton.setAttribute('aria-description', this.strings.voice_unsupported);
        const desktop = window.matchMedia('(min-width: 990px)');
        const placeholder = this.input.placeholder;
        const updatePlaceholder = () => { this.input.placeholder = desktop.matches ? placeholder : this.dataset.mobilePlaceholder || this.strings.mobile_placeholder; };
        updatePlaceholder();
        this.addEventListener('click', event => {
          const button = event.target.closest('button');
          if (!button) return;
          if (button.matches('[data-assistant-close]')) this.close();
          if (button.matches('[data-assistant-reset]')) this.reset();
          if (button.matches('[data-assistant-stop]')) this.stop();
          if (button.matches('[data-assistant-prompt]') && !button.disabled) {
            this.input.value = String(button.dataset.assistantPrompt || '').slice(0, 1000);
            this.restoredWaitingDraft = null;
            this.updateComposer(); this.persistChat();
            void this.ask(this.input.value);
          }
          if (button.matches('[data-assistant-retry]') && this.lastRequest) this.ask(this.lastRequest.text, this.lastRequest);
          if (button.matches('[data-assistant-ideas]')) this.toggleIdeas();
          if (button.matches('[data-assistant-mic]')) this.toggleVoice();
          if (button.matches('[data-assistant-copy]')) this.copyReply(button);
          if (button.matches('[data-assistant-copy-reference]')) void this.copyChatReference();
          if (button.matches('[data-mascot-photo]')) {
            this.initializeAttachments();
            this.toggleIdeas(true);
            const picker = this.querySelector('[data-attachment-pick]');
            if (picker && !picker.disabled) picker.click();
          }
        }, { signal });
        document.addEventListener('cart:updated', event => {
          if (!this.dialog?.open || this.busy || this.supportState?.mode === 'human') return;
          const cart = event.detail;
          // A quantity, discount or note update is not a successful addition.
          const confirmed = Array.isArray(cart?.addedItems) && Array.isArray(cart?.items) && cart.addedItems.some(addition => {
            const variant = String(addition?.variantId || ''), quantity = Number(addition?.quantity);
            return /^\d{1,30}$/.test(variant) && Number.isSafeInteger(quantity) && quantity > 0 && typeof addition.key === 'string' && addition.key && cart.items.some(item => item?.key === addition.key && String(item.variant_id || item.id || '') === variant);
          });
          if (confirmed) this.setMascotState('cart');
        }, { signal });
        this.input.addEventListener('input', () => { this.restoredWaitingDraft = null; this.updateComposer(); this.persistChat(); }, { signal });
        window.addEventListener?.('pagehide', () => { this.persistChat(); this.client?.detach?.(); }, { signal });
        window.addEventListener?.('pageshow', () => {
          if (this.dialog.open && (this.dialog.dataset.assistantPlatform === 'mac' || window.matchMedia('(max-width: 749px)').matches)) this.close(false, true);
          if (this.mascotState === 'welcome') this.setMascotState('welcome');
        }, { signal });
        this.addEventListener('focusin', event => {
          const rail = event.target.closest('.assistant-product-grid');
          const card = event.target.closest('[data-wishlist-card]');
          if (!rail || !card || !rail.contains(card) || rail.scrollWidth <= rail.clientWidth + 1) return;
          const bounds = rail.getBoundingClientRect(), rect = card.getBoundingClientRect();
          if (rect.left < bounds.left + 20 || rect.right > bounds.right - 20) card.scrollIntoView({ inline: 'center', block: 'nearest', behavior: document.body.classList.contains('animations-enabled') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
        }, { signal });
        this.dialog.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); this.close(); } }, { signal });
        this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, { signal });
        this.form.addEventListener('submit', event => { event.preventDefault(); this.ask(this.input.value); }, { signal });
        this.input.addEventListener('keydown', event => { if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) { event.preventDefault(); this.form.requestSubmit(); } }, { signal });
        this.dialog.addEventListener('close', () => {
          if (this.dialog.open || window.OleaHeaderPanels?.isPending(this)) { this.silentClose = false; return; }
          window.OleaHeaderPanels?.release(this);
          this.persistChat(); this.stopVoice();
          this.client?.pauseSupport?.();
          document.querySelectorAll('[data-assistant-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
          if (!this.silentClose && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
          this.silentClose = false;
        }, { signal });
        document.addEventListener('pointerdown', event => {
          if (!this.dialog.open || this.dialog.matches(':modal') || this.contains(event.target) || event.target.closest('[data-assistant-open]')) return;
          this.close(false);
        }, { signal });
        document.addEventListener('focusin', event => {
          if (!this.dialog.open || this.dialog.matches(':modal') || this.contains(event.target)) return;
          this.close(false);
        }, { signal });
        window.Olea.watchMedia(desktop, () => { updatePlaceholder(); this.close(false); }, { signal });
        this.dialog.addEventListener('click', event => {
          if (event.target !== this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close(false);
        }, { signal });
      }
      disconnectedCallback() {
        this.openSequence = (this.openSequence || 0) + 1;
        window.OleaHeaderPanels?.cancel(this);
        clearTimeout(this.closeTimer);
        clearTimeout(this.mascotAnimationTimer);
        this.persistChat();
        this.stopVoice();
        this.attachments?.destroy(); this.attachments = null;
        window.OleaHeaderPanels?.release(this);
        this.client?.detach?.();
        this.events?.abort();
        this.events = null;
      }
      prepare(endpoint) {
        if (!endpoint || this.dialog?.open) return Promise.resolve(false);
        if (!this.client || this.endpoint !== endpoint) {
          this.client?.destroy?.();
          this.client = window.OleaAssistantTransport.createClient({ endpoint, country: this.dataset.country, language: this.dataset.language });
          this.endpoint = endpoint;
          this.restoreChat(this.client.getCheckpoint?.()?.view);
        }
        return Promise.resolve(this.client.warmup?.()).then(() => true, () => false);
      }
      open(endpoint, opener, query, autoSend = false) {
        this.opener = opener;
        const sequence = this.openSequence = (this.openSequence || 0) + 1;
        clearTimeout(this.closeTimer);
        const reveal = () => {
          if (!this.isConnected || sequence !== this.openSequence) return false;
          const wasOpen = this.dialog.open;
          this.dialog.classList.remove('is-closing');
          this.silentClose = false;
          this.initializeAttachments();
          const clientChanged = !this.client || this.endpoint !== endpoint;
          if (clientChanged) {
            this.client?.destroy?.();
            this.client = window.OleaAssistantTransport.createClient({ endpoint, country: this.dataset.country, language: this.dataset.language });
            this.endpoint = endpoint;
            this.restoreChat(this.client.getCheckpoint?.()?.view);
          }
          window.OleaOverlayMotion.show(this.dialog, { modal: !window.matchMedia('(min-width: 990px)').matches });
          const localDebug = autoSend && this.isDebugCommand(query);
          if (typeof query === 'string' && query && !localDebug) {
            this.input.value = query.slice(0, 1000);
            this.restoredWaitingDraft = null;
          }
          const question = typeof query === 'string' ? query.slice(0, 1000) : '';
          const saved = this.client?.getCheckpoint?.();
          const restoringTurn = this.busy || this.resumingTurn || saved?.pendingTurn || saved?.completedTurn;
          opener?.setAttribute('aria-expanded', 'true');
          this.updateComposer();
          if (!this.busy) this.restoreMascotState();
          const connectNeeded = !wasOpen || clientChanged || !this.supportState && !this.supportConnecting || this.serviceIssue;
          if (connectNeeded) this.supportReadyPromise = this.startSupport();
          const ready = connectNeeded || this.supportConnecting ? this.supportReadyPromise : Promise.resolve(!!this.supportState && !this.serviceIssue);
          if (localDebug) void this.ask(question);
          else if (autoSend && question.trim() && !restoringTurn) void Promise.resolve(ready).then(connected => {
            if (!connected || !this.isConnected || !this.dialog.open || sequence !== this.openSequence || this.busy || this.resumingTurn || this.input.value !== question || this.supportConnecting || !this.supportState || this.supportState.chatBanned || this.supportState.mode === 'closed' || this.supportState.canSend === false) return;
            void this.ask(question);
          });
          this.input.focus();
          if (!wasOpen || clientChanged) this.hydrateSavedCards();
          this.persistChat();
          return true;
        };
        return window.OleaHeaderPanels ? window.OleaHeaderPanels.open('assistant', this, reveal) : Promise.resolve(reveal());
      }
      closeForHeader() { return this.close(false); }
      close(restoreFocus = true, immediate = false) {
        this.openSequence = (this.openSequence || 0) + 1;
        window.OleaHeaderPanels?.cancel(this);
        clearTimeout(this.closeTimer);
        this.silentClose = !restoreFocus;
        this.persistChat(); this.stopVoice(); this.toggleIdeas(false);
        this.client?.pauseSupport?.();
        if (!this.dialog.open) { window.OleaHeaderPanels?.release(this); return window.OleaOverlayMotion.finished(this.dialog); }
        return window.OleaOverlayMotion.hide(this.dialog, { immediate, after: () => window.OleaHeaderPanels?.release(this) });
      }
      initializeAttachments() {
        if (this.attachments || !window.OleaAssistantAttachments) return;
        const root = this.querySelector('assistant-attachments');
        if (!root) return;
        this.attachments = window.OleaAssistantAttachments.create({ root, strings: this.strings.photos,
          onChange: () => this.updateComposer(), onStatus: message => { if (this.serviceIssue) this.updateSupportStatus(); else this.status.textContent = message; } });
      }
      getPhotos() { return this.attachments?.getImages() || []; }
      initializeMascot() {
        const read = selector => {
          const source = this.querySelector(selector);
          try { const value = JSON.parse(source?.content?.textContent || source?.textContent || '{}'); return value && typeof value === 'object' && !Array.isArray(value) ? value : {}; } catch { return {}; }
        };
        this.pageContext = read('[data-assistant-page-context]');
        this.blogContext = read('[data-assistant-blog-context]');
        this.mascotAssets = read('[data-mascot-assets]');
        this.mascotStrings = read('[data-mascot-strings]');
        this.mascotCard = this.querySelector('[data-assistant-mascot-card]');
        this.mascotProgress = this.querySelector('[data-mascot-progress]');
        this.mascotCompletedSteps = new Set();
        this.mascotVisitedSteps = new Set();
        this.setMascotState('welcome');
      }
      getMascotState(state) {
        const names = { welcome: 'salut', thinking: 'reflechit', searching: 'lunettes', writing: 'carnet', answer: 'presente', idea: 'idee', cart: 'danse', order: 'pouce', team: 'telephone', error: 'assis' };
        const key = Object.hasOwn(names, state) ? state : 'welcome';
        const strings = this.mascotStrings?.states || this.mascotStrings || {};
        let currentProduct = this.pageContext?.type === 'product';
        if (currentProduct && this.pageContext.url) { try { currentProduct = new URL(this.pageContext.url, location.origin).pathname === location.pathname; } catch { currentProduct = false; } }
        const productWelcome = key === 'welcome' && currentProduct && !this.chatNodes?.length && this.supportState?.mode !== 'human' ? this.pageContext.welcome : null;
        return { key, image: this.mascotAssets?.[names[key]] || this.mascotAssets?.[key], copy: productWelcome || strings[key] || {} };
      }
      setMascotState(state) {
        if (!this.dialog) return;
        const next = this.getMascotState(state);
        const changed = this.mascotState !== next.key;
        this.mascotState = this.dialog.dataset.mascotState = next.key;
        if (this.mascotCard) this.mascotCard.dataset.mascotState = next.key;
        for (const [part, value] of Object.entries(next.copy)) if (['eyebrow', 'title', 'description'].includes(part) && typeof value === 'string') {
          const node = this.querySelector(`[data-mascot-${part}]`); if (node) node.textContent = value;
        }
        const image = this.querySelector('[data-mascot-image]');
        if (image && typeof next.image === 'string' && next.image) {
          try {
            const url = new URL(next.image, location.origin);
            if (['http:', 'https:'].includes(url.protocol) && !url.username && !url.password && image.src !== url.href) image.src = url.href;
          } catch {}
        }
        const step = { thinking: 'think', searching: 'search', writing: 'write' }[next.key];
        if (next.key === 'thinking' && changed) { this.mascotCompletedSteps.clear(); this.mascotVisitedSteps.clear(); }
        if (step) {
          const order = ['think', 'search', 'write'];
          for (const visited of this.mascotVisitedSteps) {
            if (order.indexOf(visited) < order.indexOf(step)) this.mascotCompletedSteps.add(visited);
            else this.mascotCompletedSteps.delete(visited);
          }
          this.mascotVisitedSteps.add(step);
        } else if (['answer', 'idea', 'order'].includes(next.key)) for (const visited of this.mascotVisitedSteps) this.mascotCompletedSteps.add(visited);
        if (this.mascotProgress) {
          this.mascotProgress.hidden = !step;
          this.mascotProgress.querySelectorAll('[data-step]').forEach(node => {
            const active = node.dataset.step === step, complete = !active && this.mascotCompletedSteps.has(node.dataset.step);
            node.dataset.status = active ? 'active' : complete ? 'complete' : 'waiting';
            node.classList.toggle('is-active', active); node.classList.toggle('is-complete', complete);
            if (active) node.setAttribute('aria-current', 'step'); else node.removeAttribute('aria-current');
            const label = node.querySelector('[data-step-label]'), text = this.mascotStrings?.steps?.[node.dataset.step];
            if (label && typeof text === 'string') label.textContent = text;
          });
        }
        if (changed && this.mascotCard) {
          clearTimeout(this.mascotAnimationTimer);
          const animate = document.body.classList.contains('animations-enabled') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.mascotCard.classList.toggle('is-changing', animate);
          if (animate) this.mascotAnimationTimer = setTimeout(() => this.mascotCard?.classList.remove('is-changing'), 180);
        }
        if (changed) this.dispatchEvent(new CustomEvent('olea:mascot-state', { bubbles: true, detail: { state: next.key } }));
      }
      mascotResultState(result) {
        const actions = Array.isArray(result?.navigationActions) ? result.navigationActions : [];
        for (const action of actions) {
          if (!['tracking', 'article'].includes(action?.kind)) continue;
          try {
            const url = new URL(action.url, location.origin);
            if (url.protocol !== 'https:' || url.username || url.password || url.origin !== location.origin) continue;
            if (action.kind === 'tracking') return 'order';
            if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?blogs\//i.test(url.pathname)) return 'idea';
          } catch {}
        }
        return 'answer';
      }
      restoreMascotState() {
        if (this.supportState?.chatBanned || this.supportState?.mode === 'closed' || this.serviceIssue) { this.setMascotState('error'); return; }
        if (this.supportState?.mode === 'human') { this.setMascotState('team'); return; }
        const latest = [...(this.chatNodes || [])].reverse().find(node => node.role === 'assistant' && node.message?.parentElement === this.feed && !node.message.classList.contains('is-thinking') && (node !== this.pending || node.completed));
        this.setMascotState(latest?.message?.classList.contains('is-error') ? 'error' : latest ? this.mascotResultState(latest.result) : 'welcome');
      }
      showUserQuestion(text, images) {
        const node = this.addMessage('user', text || this.strings.photo_message);
        if (images.length) {
          const pictures = document.createElement('div'); pictures.className = 'assistant-message__photos';
          images.forEach(photo => { const image = document.createElement('img'); image.src = photo.dataUrl; image.alt = photo.name || this.strings.photo_message; image.width = 120; image.height = 120; pictures.append(image); });
          node.body.append(pictures);
        }
        return node;
      }
      toggleIdeas(show = this.ideas.hidden) {
        this.ideas.hidden = !show;
        this.ideasButton.setAttribute('aria-expanded', String(show));
      }
      updateComposer() {
        const restricted = this.supportState?.chatBanned || this.supportState?.mode === 'closed' || this.supportState?.canSend === false;
        const awaitingConnection = this.supportConnecting || !this.supportState;
        const localDebug = this.isDebugCommand(this.input.value);
        this.submitButton.disabled = !localDebug && (this.busy || restricted || awaitingConnection || this.attachments?.busy || (!this.input.value.trim() && !this.getPhotos().length));
        // Drafting and local commands remain available; ask() still gates all remote sends.
        this.input.disabled = false;
        this.micButton.disabled = !!restricted || this.busy;
        this.querySelectorAll('button[data-assistant-prompt]').forEach(button => { button.disabled = !!(restricted || awaitingConnection || this.busy); });
        const photosBlocked = this.busy || this.supportState?.chatBanned || this.supportState?.mode === 'closed' || this.supportState?.canSend === false || this.supportState?.mode === 'human';
        this.attachments?.setDisabled(!!photosBlocked, this.busy ? '' : this.supportState?.mode === 'human' ? this.strings.human_photos : this.strings.photos.disabled);
        const count = this.getPhotos().length;
        if (count) this.ideasButton.dataset.photoCount = String(count); else delete this.ideasButton.dataset.photoCount;
        this.input.style.height = '44px';
        if (this.input.value) this.input.style.height = `${Math.min(120, this.input.scrollHeight)}px`;
        else this.input.style.height = window.matchMedia('(max-width:989px)').matches ? '40px' : '44px';
      }
      async startSupport() {
        if (!this.dialog?.open || !this.client?.connectSupport) return false;
        this.supportConnecting = true; this.updateComposer();
        try { await this.client.connectSupport(state => this.applySupportState(state)); if (this.dialog?.open) await this.resumePending(); return this.dialog?.open === true; }
        catch (error) { if (this.dialog?.open) { this.setMascotState('error'); if (error.code === 'usage_exceeded') this.showQuotaIssue('startup'); else if (this.serviceIssue) this.updateSupportStatus(); else this.status.textContent = this.supportText('unavailable'); } return false; }
        finally { this.supportConnecting = false; this.updateComposer(); if (this.restoredPhotosMissing && !this.busy && !this.serviceIssue && this.supportState?.mode === 'ai') this.status.textContent = this.runtimeText('photos_restore'); }
      }
      supportText(key, variables = {}) {
        const fallback = { team: 'Équipe Oléa', information: 'Information de la discussion', certified: 'Équipe Oléa · marque', manual_header: 'Équipe Oléa · échange en direct', manual_profile_header: '{name} · équipe Oléa', manual_status: 'Vous échangez avec l’équipe Oléa', manual_profile_status: 'Vous échangez avec {name} de l’équipe Oléa', banned_header: 'Accès au chat suspendu', closed_header: 'Conversation terminée', banned: 'Votre accès à cette discussion est suspendu.', closed: 'Cette conversation est terminée. Vous pouvez démarrer un nouvel échange.', unavailable: 'La connexion au service de discussion est momentanément indisponible. Réessayez en rouvrant le chat.', unconfirmed: 'Votre message n’a pas pu être confirmé. Votre texte est conservé ; réessayez.', sending: 'Envoi en cours', sent: 'Message envoyé', failed: 'Message non confirmé', quota: hostingQuotaMessage };
        Object.assign(fallback, { chat_reference: 'Référence du chat : {reference}', copy_reference: 'Copier', reference_copied: 'Référence copiée', reference_copy_failed: 'Copie indisponible. Référence complète : {reference}' });
        return window.OleaSupportI18n?.text(key, variables, this.dataset?.language || document.documentElement.lang) || String(fallback[key] || '').replace(/\{(\w+)\}/g, (match, name) => variables[name] == null ? match : String(variables[name]));
      }
      runtimeText(key) { return window.OleaChatRuntime?.text(key, this.dataset?.language || document.documentElement.lang) || this.strings.connection_error || key; }
      isRejectedModelResult(result) { return result?.api?.structuredOutput?.valid === false; }
      persistChat() {
        if (!this.client?.saveView || this.restoringView) return;
        const positions = new Map(Array.from(this.feed?.children || [], (node, index) => [node, index]));
        const entries = (this.chatNodes || []).filter(node => !node.message.classList.contains('is-thinking') && (node.message.parentElement === this.feed || node.message.parentElement === undefined) && !node.message.removed).sort((a, b) => (positions.get(a.message) ?? Infinity) - (positions.get(b.message) ?? Infinity)).slice(-40).map(node => ({ role: node.role, text: String(node.body.oleaText ?? node.text ?? '').slice(0, 16000), error: node.message.classList.contains('is-error'), modelRejected: node.modelRejected === true, rejectedReply: node.modelRejected ? String(node.rejectedReply ?? node.body.oleaText ?? node.text ?? '').slice(0, 2400) : undefined, profile: node.profile, turnId: node.turnId, id: node.message.dataset.supportMessageId, seq: Number(node.message.dataset.supportSeq) || null, result: window.OleaChatRuntime?.result(node.result) || null }));
        const view = { debugEnabled: this.debugEnabled === true, draft: String(this.input?.value || '').slice(0, 1000), waitingText: this.busy && !this.lastRequest?.cancelled ? String(this.lastRequest?.text || '').slice(0, 1000) : '', hadPhotos: this.getPhotos().length > 0, entries, history: (this.history || []).slice(-8) };
        while (JSON.stringify(view).length > 100000 && entries.length > 1) entries.shift();
        if (JSON.stringify(view).length > 100000 && entries[0]) entries[0].result = null;
        this.client.saveView(view);
      }
      restoreChat(value) {
        if (typeof value?.debugEnabled === 'boolean') { this.debugEnabled = value.debugEnabled; this.repaintDebug(); }
        if (!value || !Array.isArray(value.entries) || (this.chatNodes || []).length) return;
        this.restoringView = true;
        try {
          for (const entry of value.entries.slice(-40)) {
            if (!['user', 'assistant', 'human', 'system'].includes(entry?.role) || typeof entry.text !== 'string' || entry.text.length > 16000) continue;
            const node = this.addMessage(entry.role, entry.text, entry.profile);
            node.modelRejected = entry.role === 'assistant' && (entry.modelRejected === true || this.isRejectedModelResult(entry.result));
            if (node.modelRejected) node.rejectedReply = typeof entry.rejectedReply === 'string' ? entry.rejectedReply.slice(0, 2400) : entry.text;
            if (entry.error === true || node.modelRejected) { node.message.classList.add('is-error'); this.lastError = node.message; }
            if (typeof entry.turnId === 'string' && /^[a-z0-9-]{16,64}$/i.test(entry.turnId)) node.turnId = entry.turnId;
            if (typeof entry.id === 'string' && entry.id.length <= 120) { node.message.dataset.supportMessageId = entry.id; this.supportSeen.add(entry.id); }
            if (Number.isSafeInteger(entry.seq) && entry.seq > 0) node.message.dataset.supportSeq = String(entry.seq);
            node.result = window.OleaChatRuntime?.result(entry.result);
          }
          this.orderSupportMessages();
          for (const node of this.chatNodes) if (node.result && !node.message.classList.contains('is-error')) { this.renderActions(node.content, node.result.navigationActions); this.renderSuggestions(node.content, node.result.suggestions); }
          if (typeof value.draft === 'string') this.input.value = value.draft.slice(0, 1000);
          const checkpoint = this.client.getCheckpoint?.();
          if (!this.input.value && !checkpoint?.pendingTurn && !checkpoint?.completedTurn && typeof value.waitingText === 'string') { this.input.value = value.waitingText.slice(0, 1000); this.restoredWaitingDraft = this.input.value || null; }
          const savedHistory = Array.isArray(value.history) ? value.history.filter(item => ['user', 'assistant'].includes(item?.role) && typeof item.content === 'string').slice(-8).map(item => ({ role: item.role, content: item.content.slice(0, 1600) })) : [];
          let historyCursor = this.chatNodes.length - 1;
          this.history = savedHistory.reverse().filter(item => {
            for (let index = historyCursor; index >= 0; index--) {
              const node = this.chatNodes[index];
              if (node.role !== item.role || String(node.body.oleaText ?? node.text ?? '').slice(0, 1600) !== item.content) continue;
              historyCursor = index - 1;
              return item.role !== 'assistant' || !node.message.classList.contains('is-error');
            }
            return true;
          }).reverse();
          if (value.hadPhotos) { this.restoredPhotosMissing = true; this.status.textContent = this.runtimeText('photos_restore'); }
        } finally { this.restoringView = false; this.orderSupportMessages(); this.repaintDebug(); }
      }
      async resumePending() {
        if (!this.dialog?.open || this.busy || !this.client?.resume || this.supportState?.mode !== 'ai' || this.supportState?.chatBanned || this.supportState?.canSend === false) return;
        const saved = this.client.getCheckpoint?.();
        const turn = saved?.pendingTurn || saved?.completedTurn;
        if (!turn || turn.stopped || this.resumingTurn === turn.turnId || this.isDebugCommand(turn.text)) return;
        if (saved.completedTurn && this.chatNodes?.some(node => node.role === 'assistant' && node.turnId === turn.turnId && node.result)) { this.client.acknowledgeResult?.(); return; }
        this.resumingTurn = turn.turnId;
        if (!this.chatNodes?.some(node => node.role === 'user') && saved.pendingTurn) this.showUserQuestion(turn.text, []);
        try { await this.ask(saved.pendingTurn?.text || '', { text: saved.pendingTurn?.text || '', turnId: turn.turnId, images: [], humanDisplayed: true, restoring: true }); }
        finally { this.resumingTurn = null; }
      }
      hydrateSavedCards() {
        for (const node of this.chatNodes || []) if (node.result && !node.cardsRendered && this.dialog?.open) { node.cardsRendered = true; void this.renderProducts(node.content, node.result, new AbortController().signal, this.version); }
      }
      repaintDebug() {
        this.dataset.assistantDebugEnabled = String(this.debugEnabled === true);
        if (this.dialog) this.dialog.dataset.assistantDebugEnabled = this.dataset.assistantDebugEnabled;
        for (const node of this.chatNodes || []) {
          node.content.querySelectorAll?.('[data-assistant-debug]').forEach(item => item.remove());
          if (this.debugEnabled && node.role === 'assistant' && node.result?.api) {
            window.OleaChatRuntime?.debug(node.content, node.result.api, this.dataset?.language || document.documentElement.lang, document);
            node.content.querySelectorAll?.('[data-assistant-debug]').forEach(item => { item.hidden = false; item.open = true; });
          }
        }
      }
      isDebugCommand(value) {
        return typeof value === 'string' && value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s/g, '').toLowerCase() === 'oleadebug';
      }
      localizeInterface() {
        const disclosure = this.querySelector('.assistant-disclosure');
        if (disclosure && window.OleaChatRuntime) { disclosure.title = this.runtimeText('disclosure'); (disclosure.querySelector('[data-assistant-disclosure-text]') || disclosure).textContent = disclosure.dataset.approvedText || disclosure.title; }
        if (!window.OleaSupportI18n) return;
        for (const [key, value] of Object.entries(this.strings)) { if (typeof value !== 'string') continue; const sourceKey = key === 'assistant' ? 'title' : key; const translated = this.supportText(sourceKey); if (translated && translated !== sourceKey) this.strings[key] = translated; }
        if (this.strings.photos && typeof this.strings.photos === 'object') for (const [key, sourceKey] of Object.entries({ pickLabel: 'pick_photo', removeLabel: 'remove_photo', limitError: 'photo_limit', typeError: 'photo_type', sizeError: 'photo_size', dimensionsError: 'photo_dimensions', decodeError: 'photo_decode', compressError: 'photo_compress', processing: 'photo_processing', ready: 'photo_ready', disabled: 'photo_unavailable' })) this.strings.photos[key] = this.supportText(sourceKey);
        const text = (selector, key) => { const node = this.querySelector(selector); if (node) node.textContent = this.supportText(key); };
        const attribute = (selector, name, key) => this.querySelector(selector)?.setAttribute?.(name, this.supportText(key));
        const ownText = (node, value) => { if (!node) return; const content = Array.from(node.childNodes || []).find(child => child.nodeType === 3 && child.textContent.trim()); if (content) content.textContent = value; else node.append?.(document.createTextNode(value)); };
        text('.assistant-welcome h3', 'welcome_title'); text('.assistant-welcome > .muted', 'welcome_text'); text('[data-assistant-reset] > span', 'new_conversation'); text('[data-assistant-stop]', 'stop'); text('label[for="OleaAssistantInput"]', 'input_label'); text('.assistant-escape', 'escape'); text('.assistant-identity .olea-beta', 'beta');
        attribute('[data-assistant-reset]', 'aria-label', 'new_conversation'); attribute('[data-assistant-close]', 'aria-label', 'close'); attribute('[data-assistant-send]', 'aria-label', 'send'); attribute('[data-assistant-mic]', 'aria-label', 'voice'); attribute('[data-assistant-ideas]', 'aria-label', 'show_ideas'); attribute('[data-assistant-feed]', 'aria-label', 'conversation'); attribute('textarea', 'placeholder', 'panel_placeholder');
        if (this.dataset.mascotPlaceholder) this.input.placeholder = this.dataset.mascotPlaceholder;
        if (this.querySelector('assistant-attachments')) attribute('[data-assistant-ideas]', 'aria-label', 'attachments');
        text('.assistant-photo-hint', 'photo_hint'); ownText(this.querySelector('[data-attachment-pick]'), this.supportText('pick_photo'));
        for (const node of this.querySelectorAll('[data-assistant-ui]')) node.textContent = this.supportText(node.dataset.assistantUi);
        for (const node of document.querySelectorAll('[data-assistant-open-label]')) node.textContent = this.supportText('open_label');
        if (this.identityTitle) ownText(this.identityTitle, this.supportText('title') + ' ');
        if (this.identityStatusText) this.identityStatusText.textContent = this.supportText(this.identityStatusDot ? 'online_status' : 'neutral_status');
        for (const prompt of this.querySelectorAll('[data-assistant-prompt-key]')) { const value = this.supportText(prompt.dataset.assistantPromptKey); prompt.dataset.assistantPrompt = value; const label = prompt.querySelector('span'); if (label) label.textContent = value; else ownText(prompt, value); }
        text('.assistant-starter-prompts a.assistant-starter-prompt--mobile > span', 'contact_team');
        const contact = this.querySelector('[data-assistant-contact]')?.content?.querySelector?.('a');
        if (contact) ownText(contact, this.supportText(contact.href?.startsWith('mailto:') ? 'email' : 'write_whatsapp'));
      }
      chatReference() {
        const reference = this.supportState?.visitorReference;
        return this.supportState?.chatBanned && typeof reference === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(reference) ? reference : '';
      }
      updateChatReference() {
        if (!this.banReference) return;
        const reference = this.chatReference(); this.banReference.hidden = !reference;
        if (this.referenceLabel) this.referenceLabel.textContent = reference ? this.supportText('chat_reference', { reference: reference.slice(0, 8) }) : '';
        if (this.referenceCopyButton) { this.referenceCopyButton.textContent = this.supportText('copy_reference'); this.referenceCopyButton.disabled = !reference; this.referenceCopyButton.setAttribute('aria-label', this.supportText('copy_reference') + ' · ' + this.supportText('chat_reference', { reference: reference.slice(0, 8) })); }
        if (this.referenceStatus) this.referenceStatus.textContent = '';
      }
      async copyChatReference() {
        const reference = this.chatReference(); if (!reference) return;
        try { await navigator.clipboard.writeText(reference); if (this.chatReference() === reference && this.referenceStatus) this.referenceStatus.textContent = this.supportText('reference_copied'); }
        catch { if (this.chatReference() === reference && this.referenceStatus) this.referenceStatus.textContent = this.supportText('reference_copy_failed', { reference }); }
      }
      advisorName(profile) { return typeof profile?.name === 'string' ? profile.name.replace(/\s+/g, ' ').trim().slice(0, 60) : ''; }
      advisorAvatarUrl(profile) {
        if (typeof profile?.avatarUrl !== 'string' || profile.avatarUrl.length > 2048) return '';
        try { const url = new URL(profile.avatarUrl); return url.protocol === 'https:' && !url.username && !url.password && (url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/') || ['heyolea.com', 'www.heyolea.com'].includes(url.hostname) && url.pathname.startsWith('/cdn/shop/')) ? url.href : ''; } catch { return ''; }
      }
      brandBadge() {
        const badge = document.createElement('span'); badge.className = 'assistant-brand-badge'; badge.setAttribute('role', 'img'); badge.setAttribute('aria-label', this.supportText('certified')); badge.title = this.supportText('certified');
        const mark = document.createElement('span'); mark.textContent = '✓'; mark.setAttribute('aria-hidden', 'true'); badge.append(mark); return badge;
      }
      advisorAvatar(profile) {
        const avatar = document.createElement('span'); avatar.className = 'assistant-avatar assistant-avatar--advisor'; avatar.setAttribute('aria-hidden', 'true');
        const name = this.advisorName(profile) || 'Oléa', initials = name.split(' ').slice(0, 2).map(part => Array.from(part)[0] || '').join('').toLocaleUpperCase();
        const fallback = document.createElement('span'); fallback.className = 'assistant-avatar__initials'; fallback.textContent = initials; avatar.append(fallback);
        const url = this.advisorAvatarUrl(profile);
        if (url) { const image = document.createElement('img'); image.className = 'assistant-avatar__advisor-image'; image.src = url; image.alt = ''; image.width = 40; image.height = 40; image.loading = 'lazy'; image.decoding = 'async'; image.referrerPolicy = 'no-referrer'; image.addEventListener('error', () => image.remove(), { once: true }); avatar.append(image); }
        return avatar;
      }
      showQuotaIssue(source, request) {
        if (source !== 'startup' || this.serviceIssue?.source !== 'send') this.serviceIssue = { code: 'usage_exceeded', source, turnId: request?.turnId };
        this.setMascotState('error');
        this.updateSupportStatus();
      }
      updateSupportStatus() {
        const state = this.supportState;
        const quota = this.serviceIssue?.code === 'usage_exceeded' && !state?.chatBanned && state?.mode !== 'closed';
        const name = this.advisorName(state?.advisorProfile);
        this.status.textContent = state?.chatBanned ? this.supportText('banned') : state?.mode === 'closed' ? this.supportText('closed') : quota ? this.supportText('quota') : state?.mode === 'human' ? this.supportText(name ? 'manual_profile_status' : 'manual_status', { name }) : '';
        if (quota) this.status.classList.add('is-service-issue'); else this.status.classList.remove('is-service-issue');
      }
      updateSupportIdentity(state) {
        const name = this.advisorName(state?.advisorProfile);
        if (this.identityStatusText) this.identityStatusText.textContent = state?.chatBanned ? this.supportText('banned_header') : state?.mode === 'closed' ? this.supportText('closed_header') : state?.mode === 'human' ? this.supportText(name ? 'manual_profile_header' : 'manual_header', { name }) : this.identityDefaultStatus;
        if (this.identityStatusDot) this.identityStatusDot.hidden = state?.chatBanned || state?.mode === 'closed' ? true : this.identityDefaultDotHidden;
        const identityKey = state?.mode === 'human' ? JSON.stringify([name, this.advisorAvatarUrl(state.advisorProfile)]) : 'ai';
        if (identityKey !== this.identityKey) {
          this.identityKey = identityKey;
          if (this.identityTitle) { if (state?.mode === 'human') { const label = document.createElement('bdi'); label.textContent = name || this.supportText('team'); label.setAttribute('translate', 'no'); this.identityTitle.replaceChildren(label, this.brandBadge()); } else this.identityTitle.replaceChildren(...this.identityDefaultTitle.map(node => node.cloneNode(true))); }
          if (this.identityAvatar) { if (state?.mode === 'human') { const portrait = this.advisorAvatar(state.advisorProfile); this.identityAvatar.replaceChildren(...Array.from(portrait.childNodes)); this.identityAvatar.classList.add('assistant-avatar--advisor'); } else { this.identityAvatar.replaceChildren(...this.identityDefaultAvatar.map(node => node.cloneNode(true))); this.identityAvatar.classList.remove('assistant-avatar--advisor'); } }
        }
      }
      applySupportState(state) {
        const previousMode = this.supportState?.mode;
        this.supportState = state; this.supportConnecting = false;
        if (this.serviceIssue?.source === 'startup') this.serviceIssue = null;
        this.updateSupportIdentity(state);
        this.updateChatReference();
        if ((state.chatBanned || state.mode !== 'ai') && this.busy && !this.sendingHuman) {
          const photoRequest = this.lastRequest?.images?.length ? this.lastRequest : null;
          this.version++; this.controller?.abort(); this.client?.stop();
          if (!this.pending?.completed) this.pending?.message?.remove();
          this.pending = null; this.lastRequest = photoRequest;
          if (photoRequest && !this.input.value.trim()) this.input.value = photoRequest.text;
          this.setBusy(false);
        }
        for (const entry of state.messages || []) {
          if (!['human', 'system'].includes(entry.role) || this.supportSeen.has(entry.id)) continue;
          this.supportSeen.add(entry.id);
          const node = this.addMessage(entry.role, entry.text, entry.advisorProfile || state.advisorProfile);
          node.message.dataset.supportMessageId = entry.id;
          if (Number.isSafeInteger(Number(entry.seq)) && Number(entry.seq) > 0) node.message.dataset.supportSeq = String(entry.seq);
          const time = node.meta.querySelector('time');
          if (time && Number.isFinite(Date.parse(entry.createdAt))) { const date = new Date(entry.createdAt); time.dateTime = date.toISOString(); time.textContent = date.toLocaleTimeString(document.documentElement.lang, { hour: '2-digit', minute: '2-digit' }); }
        }
        this.recoverCanonicalMessages(state);
        this.reconcileSupportUsers(state);
        this.orderSupportMessages();
        this.updateSupportStatus();
        this.syncBlogLinks();
        if (this.welcome) this.welcome.hidden = !!this.chatNodes?.length || state.mode !== 'ai' || state.chatBanned;
        if (state.chatBanned || state.mode === 'closed') this.setMascotState('error');
        else if (state.mode === 'human') this.setMascotState('team');
        else if (!this.busy && (previousMode && previousMode !== 'ai' || this.mascotState === 'error' && !this.lastError && !this.serviceIssue)) this.restoreMascotState();
        if (state.mode === 'human' && this.getPhotos().length && !this.serviceIssue) this.status.textContent = this.strings.human_photos;
        this.setBusy(this.busy === true); this.updateComposer();
        this.persistChat();
        if (!this.busy && this.client?.getCheckpoint && this.client.getCheckpoint()?.pendingTurn && !this.resumingTurn) void this.resumePending();
      }
      recoverCanonicalMessages(state) {
        if (this.busy || this.pending || this.lastRequest || !this.client?.getCheckpoint) return;
        const checkpoint = this.client.getCheckpoint();
        if (checkpoint?.pendingTurn || checkpoint?.completedTurn) return;
        this.chatNodes ||= [];
        let changed = false;
        const messages = [...(state.messages || [])].filter(entry => ['user', 'assistant'].includes(entry.role) && typeof entry.id === 'string' && typeof entry.text === 'string' && Number.isSafeInteger(Number(entry.seq)) && Number(entry.seq) > 0).sort((a, b) => Number(a.seq) - Number(b.seq));
        for (const entry of messages) {
          if (this.supportSeen.has(entry.id)) continue;
          const text = entry.text.trim();
          const node = this.chatNodes.find(node => node.role === entry.role && !node.message.dataset.supportMessageId && !node.message.classList.contains('is-thinking') && (!node.message.classList.contains('is-error') || node.modelRejected === true) && String(node.modelRejected ? node.rejectedReply ?? node.body.oleaText ?? node.text ?? '' : node.body.oleaText ?? node.text ?? '').trim() === text) || this.addMessage(entry.role, entry.text);
          node.message.dataset.supportMessageId = entry.id; node.message.dataset.supportSeq = String(entry.seq); node.message.dataset.delivery = 'confirmed';
          const time = node.meta.querySelector('time');
          if (time && Number.isFinite(Date.parse(entry.createdAt))) { const date = new Date(entry.createdAt); time.hidden = false; time.dateTime = date.toISOString(); time.textContent = date.toLocaleTimeString(document.documentElement.lang, { hour: '2-digit', minute: '2-digit' }); }
          else if (time) time.hidden = true;
          this.supportSeen.add(entry.id); changed = true;
        }
        if (changed) this.history = this.chatNodes.filter(node => ['user', 'assistant'].includes(node.role) && !node.message.classList.contains('is-thinking') && !node.message.classList.contains('is-error')).sort((a, b) => (Number(a.message.dataset.supportSeq) || Infinity) - (Number(b.message.dataset.supportSeq) || Infinity)).slice(-8).map(node => ({ role: node.role, content: String(node.body.oleaText ?? node.text ?? '').slice(0, 1600) }));
        if (this.restoredWaitingDraft && this.input.value === this.restoredWaitingDraft) {
          const question = [...this.chatNodes].reverse().find(node => node.role === 'user' && String(node.body.oleaText ?? node.text ?? '').trim() === this.restoredWaitingDraft.trim() && Number(node.message.dataset.supportSeq) > 0);
          if (question && this.chatNodes.some(node => node.role === 'assistant' && !node.message.classList.contains('is-error') && Number(node.message.dataset.supportSeq) > Number(question.message.dataset.supportSeq))) { this.input.value = ''; this.restoredWaitingDraft = null; }
        }
      }
      reconcileSupportUsers(state) {
        if (!this.supportLocalTurns?.length) return;
        for (const entry of [...(state.messages || [])].filter(entry => entry.role === 'user' && Number.isSafeInteger(Number(entry.seq)) && Number(entry.seq) > 0).sort((a, b) => Number(a.seq) - Number(b.seq))) {
          const index = this.supportLocalTurns.findIndex(turn => turn.conversationId === state.conversationId && Number(entry.seq) > turn.afterSeq && turn.text === String(entry.text ?? entry.content ?? '').trim());
          if (index < 0) continue;
          const [turn] = this.supportLocalTurns.splice(index, 1);
          turn.node.dataset.supportSeq = String(entry.seq); turn.node.dataset.supportMessageId = entry.id;
          delete turn.node.dataset.supportPending;
        }
      }
      orderSupportMessages() {
        if (!this.feed?.insertBefore || !this.feed.children) return;
        const nodes = Array.from(this.feed.children).filter(node => Number(node.dataset?.supportSeq) > 0 || node.dataset?.supportPending === 'true');
        const ordered = [...nodes].sort((a, b) => (Number(a.dataset.supportSeq) || Infinity) - (Number(b.dataset.supportSeq) || Infinity));
        if (nodes.every((node, index) => node === ordered[index])) return;
        // Empty anchors preserve every non-service AI node in its existing slot.
        const anchors = nodes.map(node => { const anchor = document.createTextNode(''); this.feed.insertBefore(anchor, node); return anchor; });
        ordered.forEach((node, index) => { this.feed.insertBefore(node, anchors[index]); anchors[index].remove(); });
      }
      async askHuman(text, request) {
        this.setMascotState('team');
        const version = ++this.version;
        if (!request.humanNode && !request.humanDisplayed) request.humanNode = this.addMessage('user', text);
        if (request.humanNode && !request.humanNode.message.dataset.supportSeq) {
          this.supportLocalTurns ||= [];
          if (!this.supportLocalTurns.some(turn => turn.turnId === request.turnId)) this.supportLocalTurns.push({ turnId: request.turnId, conversationId: this.supportState?.conversationId, afterSeq: Number(this.supportState?.cursor) || 0, text: String(text).trim(), node: request.humanNode.message });
          request.humanNode.message.dataset.supportPending = 'true';
        }
        if (request.humanNode) { request.humanNode.message.dataset.delivery = 'pending'; request.humanNode.meta.setAttribute('aria-label', this.supportText('sending')); }
        this.lastRequest = request;
        this.sendingHuman = true; this.setBusy(true);
        try {
          const result = await this.client.sendSupport({ text, operationId: request.turnId });
          if (version !== this.version) return;
          if (result.aiAllowed && this.supportState?.mode === 'ai') { this.sendingHuman = false; this.setBusy(false); await this.ask(text, request); return; }
          request.humanDisplayed = true;
          if (request.humanNode) { request.humanNode.message.dataset.delivery = 'confirmed'; request.humanNode.meta.setAttribute('aria-label', this.supportText('sent')); }
          if (String(this.input.value).trim() === text) this.input.value = ''; this.lastRequest = null;
          this.serviceIssue = null;
          this.updateSupportStatus();
        } catch (error) {
          if (version !== this.version) return;
          this.setMascotState('error');
          if (request.humanNode) { request.humanNode.message.dataset.delivery = 'failed'; request.humanNode.meta.setAttribute('aria-label', this.supportText('failed')); }
          this.input.value ||= text; this.lastRequest = request;
          if (error.code === 'usage_exceeded') this.showQuotaIssue('send', request);
          else this.status.textContent = this.supportText(error.code === 'chat_banned' ? 'banned' : error.code === 'chat_closed' ? 'closed' : 'unconfirmed');
        } finally { if (version === this.version) { this.sendingHuman = false; this.setBusy(false); } }
      }
      toggleVoice() {
        if (this.speech) { this.stopVoice(); return; }
        if (!this.Speech) { this.status.textContent = this.strings.voice_unsupported; return; }
        if (this.busy || this.micButton.disabled) return;
        let speech;
        try { speech = new this.Speech(); } catch (error) { this.status.textContent = error.name === 'NotAllowedError' ? this.strings.voice_denied : this.strings.voice_error; return; }
        this.speech = speech;
        speech.lang = document.documentElement.lang || 'fr-FR';
        speech.interimResults = false;
        speech.maxAlternatives = 1;
        this.status.textContent = this.strings.voice_listening;
        speech.onnomatch = () => { if (this.speech === speech) { this.status.textContent = this.strings.voice_no_match; this.stopVoice(); } };
        speech.onresult = event => {
          if (this.speech !== speech || !this.dialog.open) return;
          const text = event.results[0]?.[0]?.transcript;
          if (typeof text === 'string' && text.trim()) { this.input.value = text.trim().slice(0, 1000); this.updateComposer(); this.input.focus(); }
          this.status.textContent = typeof text === 'string' && text.trim() ? '' : this.strings.voice_no_match;
          this.stopVoice();
        };
        speech.onerror = event => { if (this.speech === speech) { this.status.textContent = ['not-allowed', 'service-not-allowed'].includes(event.error) ? this.strings.voice_denied : event.error === 'no-speech' ? this.strings.voice_no_match : this.strings.voice_error; this.stopVoice(); } };
        speech.onend = () => { if (this.speech === speech) { if (this.status.textContent === this.strings.voice_listening) this.status.textContent = this.strings.voice_no_match; this.stopVoice(false); } };
        this.micButton.setAttribute('aria-pressed', 'true');
        try { speech.start(); } catch (error) { this.stopVoice(); this.status.textContent = error.name === 'NotAllowedError' ? this.strings.voice_denied : this.strings.voice_error; }
      }
      stopVoice(abort = true) {
        const speech = this.speech;
        this.speech = null;
        if (this.status?.textContent === this.strings.voice_listening) this.status.textContent = '';
        this.micButton?.setAttribute('aria-pressed', 'false');
        if (!speech) return;
        speech.onresult = speech.onerror = speech.onend = speech.onnomatch = null;
        if (abort) { try { speech.abort(); } catch {} }
      }
      async copyReply(button) {
        const body = button.closest('.assistant-message')?.querySelector('.assistant-message__body');
        if (!body) return;
        try {
          await navigator.clipboard.writeText(body.innerText);
          clearTimeout(button.oleaCopyTimer);
          button.dataset.copyState = 'copied';
          button.setAttribute('aria-label', this.strings.copied);
          const icon = window.Olea.createIcon('check');
          if (icon) button.replaceChildren(icon);
          button.oleaCopyTimer = setTimeout(() => {
            if (!button.isConnected) return;
            delete button.dataset.copyState;
            button.setAttribute('aria-label', this.strings.copy);
            const original = window.Olea.createIcon('copy');
            if (original) button.replaceChildren(original);
          }, 1600);
        } catch { this.input.focus(); }
      }
      setBusy(busy) {
        this.busy = busy;
        this.micButton.disabled = busy;
        this.cancelButton.hidden = !busy || this.sendingHuman === true;
        this.form.setAttribute('aria-busy', String(busy));
        this.updateComposer();
      }
      addMessage(role, text, profile) {
        const message = document.createElement('div');
        message.className = `assistant-message assistant-message--${role}`;
        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = role === 'user' ? this.strings.you : role === 'human' ? this.advisorName(profile) || this.supportText('team') : role === 'system' ? this.supportText('information') : this.strings.assistant;
        const body = document.createElement('div');
        body.className = 'assistant-message__body';
        this.renderText(body, text);
        const content = document.createElement('div');
        content.className = 'assistant-message__content';
        content.append(body);
        const meta = document.createElement('div');
        meta.className = 'assistant-message__meta';
        if (role === 'human') { const name = document.createElement('bdi'); name.className = 'assistant-advisor-name'; name.textContent = this.advisorName(profile) || this.supportText('team'); name.setAttribute('translate', 'no'); meta.prepend(name, this.brandBadge()); body.setAttribute('translate', 'no'); }
        if (role === 'system') message.setAttribute('role', 'status');
        content.append(meta);
        if (role === 'human') message.append(this.advisorAvatar(profile));
        if (role === 'assistant') {
          const avatar = document.createElement('span');
          avatar.className = 'assistant-avatar';
          avatar.setAttribute('aria-hidden', 'true');
          avatar.append(this.querySelector('[data-assistant-avatar]').content.cloneNode(true));
          message.append(avatar);
        }
        message.append(label, content);
        this.feed.append(message);
        this.syncQuickReplies();
        this.welcome.hidden = true;
        this.follow();
        const node = { message, body, content, meta, role, text, profile };
        this.chatNodes ||= []; this.chatNodes.push(node);
        return node;
      }
      renderText(body, text, completedReply = false) {
        if (completedReply && !this.restoringView) this.setMascotState('writing');
        body.oleaText = String(text || '');
        if (window.OleaChatRuntime) { window.OleaChatRuntime.render(body, text, document); return; }
        body.replaceChildren();
        String(text || '').split(/\n\s*\n/).forEach(part => {
          const paragraph = document.createElement('p');
          const fragments = part.split(/(\*\*[^*]+\*\*)/g);
          fragments.forEach(fragment => {
            if (fragment.startsWith('**') && fragment.endsWith('**')) {
              const strong = document.createElement('strong'); strong.textContent = fragment.slice(2, -2); paragraph.append(strong);
            } else paragraph.append(document.createTextNode(fragment));
          });
          body.append(paragraph);
        });
      }
      follow() { this.viewport.scrollTop = this.viewport.scrollHeight; }
      stop() {
        if (!this.busy) return;
        if (this.lastRequest) this.lastRequest.cancelled = true;
        this.version++;
        this.controller?.abort();
        this.client?.stop();
        const notice = this.strings.stop_notice || this.strings.stopped;
        if (this.pending && !this.pending.completed) { this.pending.message.classList.remove('is-thinking'); this.renderText(this.pending.body, notice); }
        else this.addMessage('system', notice);
        this.pending = null;
        this.updateSupportStatus();
        this.setBusy(false);
        this.restoreMascotState();
        this.persistChat();
      }
      reset() {
        this.openSequence = (this.openSequence || 0) + 1;
        if (this.lastRequest) this.lastRequest.cancelled = true;
        this.stopVoice();
        this.toggleIdeas(false);
        this.stop();
        this.client?.reset();
        this.supportState = null; this.supportSeen.clear();
        this.updateSupportIdentity(null);
        this.supportLocalTurns = [];
        this.updateChatReference();
        this.serviceIssue = null; this.status.classList.remove('is-service-issue');
        this.sendingHuman = false;
        this.history = [];
        this.chatNodes = [];
        // Debug is a local display preference, preserved across a new conversation.
        this.attachments?.clear();
        this.lastRequest = null;
        this.lastError?.remove();
        this.lastError = null;
        this.feed.replaceChildren();
        this.welcome.hidden = false;
        this.setMascotState('welcome');
        this.status.textContent = '';
        this.input.value = '';
        this.persistChat();
        this.updateComposer();
        if (this.dialog.open) this.supportReadyPromise = this.startSupport();
        this.input.focus();
      }
      async ask(value, retry) {
        if (!this.dialog?.open) return;
        if (this.isDebugCommand(value)) {
          this.debugEnabled = !this.debugEnabled;
          if (this.isDebugCommand(this.input.value)) this.input.value = '';
          this.repaintDebug(); this.status.textContent = this.runtimeText(this.debugEnabled ? 'debug_on' : 'debug_off'); this.updateComposer(); this.persistChat(); return;
        }
        const text = String(value || '').trim().slice(0, 1000);
        const images = retry?.images || this.getPhotos();
        if ((!text && !images.length && !retry?.restoring) || this.attachments?.busy || this.busy || !this.client || this.supportConnecting || !this.supportState || this.supportState.chatBanned || this.supportState.mode === 'closed' || this.supportState.canSend === false) return;
        if (images.length && this.supportState.mode === 'human') { this.status.textContent = this.strings.human_photos; this.toggleIdeas(true); return; }
        // Reuse a failed human send's operation UUID to make retries idempotent.
        if (this.supportState.mode === 'human') { const request = retry || (this.lastRequest?.text === text ? this.lastRequest : { text, turnId: crypto.randomUUID() }); await this.askHuman(text, request); return; }
        this.stopVoice();
        this.toggleIdeas(false);
        this.lastError?.remove();
        this.lastError = null;
        const samePhotos = (a, b) => a.length === b.length && a.every((photo, index) => photo.dataUrl === b[index]?.dataUrl);
        const previous = retry || (this.lastRequest?.quotaFailed && this.lastRequest.text === text && samePhotos(this.lastRequest.images || [], images) ? this.lastRequest : null);
        const request = previous ? { ...previous, cancelled: false, modelRejected: false, ...(previous.cancelled || previous.modelRejected ? { turnId: crypto.randomUUID(), restoring: false } : {}) } : { text, images, history: this.history.slice(-4), turnId: crypto.randomUUID() };
        if (!request.humanDisplayed && !request.humanNode) this.showUserQuestion(text, images);
        request.humanDisplayed = true;
        this.lastRequest = request;
        if (!request.restoring) this.input.value = '';
        this.controller = new AbortController();
        const controller = this.controller;
        const version = ++this.version;
        const supportConversationId = this.supportState.conversationId;
        const pending = this.addMessage('assistant', '');
        const pendingIndicator = document.createElement('p');
        pendingIndicator.setAttribute('aria-label', this.strings.thinking);
        pending.body.replaceChildren(pendingIndicator);
        pending.turnId = request.turnId;
        this.pending = pending;
        pending.message.classList.add('is-thinking');
        this.status.textContent = '';
        this.setMascotState('thinking');
        this.setBusy(true);
        this.persistChat();
        const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(300000)]);
        try {
          const onAccepted = () => { if (!signal.aborted && version === this.version) this.setMascotState('thinking'); };
          const result = request.restoring ? await this.client.resume({ signal, onAccepted }) : await this.client.chat({ ...request, signal, onAccepted });
          if (!result) { pending.message.remove(); this.pending = null; this.restoreMascotState(); return; }
          if (signal.aborted || version !== this.version) return;
          if (this.isRejectedModelResult(result)) {
            request.modelRejected = true;
            request.rejectedReply = String(result.reply || '').slice(0, 2400);
            request.restoring = false;
            this.client.acknowledgeResult?.();
            const error = new Error('model_response_rejected');
            error.code = 'model_response_rejected';
            error.api = result.api;
            throw error;
          }
          this.serviceIssue = null; this.status.classList.remove('is-service-issue');
          pending.message.classList.remove('is-thinking');
          this.renderText(pending.body, result.reply, true);
          pending.message.classList.add('is-answer-ready');
          pending.completed = true;
          pending.result = window.OleaChatRuntime?.result(result) || { reply: result.reply, api: result.api };
          if (request.humanNode) { request.humanNode.message.dataset.delivery = 'confirmed'; request.humanNode.meta.setAttribute('aria-label', 'Message envoyé'); }
          if (!request.restoring) this.attachments?.clear();
          this.status.textContent = '';
          this.history.push({ role: 'user', content: text || this.strings.photo_message }, { role: 'assistant', content: result.reply });
          this.history = this.history.slice(-8);
          this.lastRequest = null;
          this.persistChat(); this.client.acknowledgeResult?.(); this.repaintDebug();
          await this.renderProducts(pending.content, result, signal, version);
          pending.cardsRendered = this.dialog.open;
          if (signal.aborted || version !== this.version) return;
          this.renderActions(pending.content, result.navigationActions, text);
          this.renderSuggestions(pending.content, result.suggestions);
          const copy = document.createElement('button');
          copy.type = 'button'; copy.className = 'assistant-copy'; copy.dataset.assistantCopy = ''; copy.setAttribute('aria-label', this.strings.copy);
          const icon = window.Olea.createIcon('copy');
          if (icon) copy.append(icon);
          pending.meta.append(copy);
          pending.content.append(pending.meta);
          this.status.textContent = '';
          this.setMascotState(this.mascotResultState(result));
          this.follow();
        } catch (error) {
          if (error.code === 'detached' || request.cancelled || (version !== this.version && this.lastRequest && this.lastRequest !== request)) return;
          // A mode change can occur between the state check and the job. Preserve
          // the actual visitor question using its original idempotency UUID.
          if (this.supportState?.conversationId === supportConversationId && !this.supportState.chatBanned && this.supportState.mode === 'human' && !pending.completed) {
            pending.message.remove(); this.pending = null; this.setBusy(false);
            request.humanDisplayed = true;
            if (request.images?.length) { this.lastRequest = request; this.status.textContent = this.strings.human_photos; this.toggleIdeas(true); return; }
            await this.askHuman(text, request); return;
          }
          if (error.code === 'human_active') { try { await this.client.ensureSupport(); } catch {} }
          if (error.code === 'detached' || request.cancelled || (version !== this.version && this.lastRequest && this.lastRequest !== request)) return;
          if (this.supportState?.conversationId === supportConversationId && !this.supportState.chatBanned && this.supportState.mode === 'human' && !pending.completed) {
            pending.message.remove(); this.pending = null; this.setBusy(false); request.humanDisplayed = true;
            if (request.images?.length) { this.lastRequest = request; this.status.textContent = this.strings.human_photos; this.toggleIdeas(true); return; }
            await this.askHuman(text, request); return;
          }
          if (version !== this.version || controller.signal.aborted) return;
          this.setMascotState('error');
          pending.message.classList.remove('is-thinking');
          const quota = error.code === 'usage_exceeded';
          if (request.restoring || request.modelRejected || this.client.getCheckpoint?.()?.pendingTurn) this.input.value ||= text;
          if (request.restoring && error.code === 'turn_not_found') { pending.message.remove(); this.pending = null; this.lastRequest = null; this.status.textContent = this.strings.connection_error; this.persistChat(); return; }
          const message = quota ? this.supportText('quota') : signal.aborted || error.code === 'timeout' ? this.strings.timeout : error instanceof TypeError || error.code === 'origin_forbidden' ? this.strings.connection_error : this.strings.response_error;
          this.renderText(pending.body, quota ? this.supportText('quota') : this.strings.error_help);
          if (quota) { request.quotaFailed = true; this.input.value ||= text; this.showQuotaIssue('send', request); }
          else { pending.body.firstElementChild.className = 'assistant-error-help'; const title = document.createElement('p'); title.className = 'assistant-error-title'; title.textContent = this.strings.error_title; pending.body.prepend(title); }
          pending.message.classList.add('is-error');
          pending.modelRejected = request.modelRejected === true;
          if (pending.modelRejected) pending.rejectedReply = request.rejectedReply;
          this.lastError = pending.message;
          pending.result = { reply: pending.body.oleaText || '', api: window.OleaChatRuntime?.api(error.api) };
          if (this.client.getCheckpoint?.()?.pendingTurn) this.lastRequest = { ...request, restoring: true };
          if (request.humanNode) { request.humanNode.message.dataset.delivery = 'failed'; request.humanNode.meta.setAttribute('aria-label', 'Message non confirmé'); this.input.value = text; }
          if (request.images?.length) this.toggleIdeas(true);
          this.status.textContent = message;
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'button button--outline button--small'; button.dataset.assistantRetry = ''; button.textContent = this.strings.retry;
          const actions = document.createElement('div'); actions.className = 'assistant-error-actions';
          actions.append(button, this.querySelector('[data-assistant-contact]').content.cloneNode(true));
          pending.body.append(actions);
          this.follow(); this.persistChat(); this.repaintDebug();
        } finally {
          if (version === this.version) { this.pending = null; this.setBusy(false); }
        }
      }
      syncBlogLinks() {
        const blocked = this.supportState?.chatBanned || ['human', 'closed'].includes(this.supportState?.mode);
        for (const link of this.querySelectorAll?.('[data-assistant-article]') || []) link.hidden = !!blocked;
      }
      safeArticleUrl(value) {
        if (typeof value !== 'string' || value.length > 2000 || /[\u0000-\u0020\u007f<>"\\]/.test(value)) return null;
        try {
          const url = new URL(value, location.origin);
          return url.protocol === 'https:' && !url.username && !url.password && url.origin === location.origin && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?blogs\//i.test(url.pathname) ? url : null;
        } catch { return null; }
      }
      blogFeatures(value) {
        const text = String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const families = [
          ['sofa', /\b(?:canap\w*|sofas?|couches?)\b/],
          ['chair', /\b(?:chaises?|chairs?|tabourets?|stools?)\b/],
          ['armchair', /\b(?:fauteuils?|armchairs?)\b/],
          ['table', /\b(?:tables?)\b/],
          ['fan', /\b(?:ventilateurs?|fans?)\b/],
          ['humidity', /\b(?:deshumidificat\w*|dehumidif\w*)\b/]
        ].filter(([, pattern]) => pattern.test(text)).map(([key]) => key);
        const topics = [
          ['quiet', /\b(?:bruit\w*|silenc\w*|dorm\w*|chambres?|nuits?|sleep\w*|bedrooms?|quiet\w*|nois\w*)\b/],
          ['space', /\b(?:dimension\w*|mesur\w*|tailles?|petit\w*|espace\w*|hauteur\w*|plafond\w*|siz\w*|small\w*|height\w*|ceiling\w*|space\w*)\b/],
          ['colour', /\b(?:couleur\w*|color\w*|colour\w*|teinte\w*|rouges?|jaunes?|verts?|bleus?|red|yellow|green|blue)\b/],
          ['care', /\b(?:nettoy\w*|entretien\w*|tach\w*|lav\w*|clean\w*|care|stain\w*)\b/],
          ['comfort', /\b(?:confort\w*|comfort\w*|assises?|moelleux|fermete|cushion\w*)\b/],
          ['style', /\b(?:decor\w*|styles?|ambianc\w*|tables?|bois|murs?|tapis|design\w*|interieur\w*|interior\w*|wood\w*|rugs?)\b/],
          ['energy', /\b(?:consom\w*|energi\w*|electri\w*|power)\b/],
          ['installation', /\b(?:install\w*|fixation\w*|montage\w*|mount\w*)\b/],
          ['kitchen', /\b(?:cuisin\w*|kitchens?)\b/],
          ['children', /\b(?:enfants?|bebes?|kids?|child\w*|babies|baby|nurser\w*)\b/]
        ].filter(([, pattern]) => pattern.test(text)).map(([key]) => key);
        const colours = [
          ['red', /\b(?:rouges?|red|bordeaux|grenat|burgundy)\b/],
          ['blue', /\b(?:bleu\w*|blue|navy|turquoise|teal)\b/],
          ['yellow', /\b(?:jaune\w*|yellow|moutarde|mustard)\b/],
          ['green', /\b(?:vert\w*|green|sauge|sage|olive)\b/],
          ['black', /\b(?:noir\w*|black)\b/],
          ['white', /\b(?:blanc\w*|white|ivoire|ivory)\b/],
          ['beige', /\b(?:beige\w*|ecru|sable|sand)\b/],
          ['brown', /\b(?:marron\w*|brun\w*|brown|chocolat\w*)\b/],
          ['grey', /\b(?:gris\w*|grey|gray|anthracite|charcoal)\b/],
          ['pink', /\b(?:roses?|pink|blush)\b/],
          ['orange', /\b(?:oranges?|terracotta)\b/],
          ['purple', /\b(?:violet\w*|purple|prune|plum)\b/]
        ].filter(([, pattern]) => pattern.test(text)).map(([key]) => key);
        if (colours.length && !topics.includes('colour')) topics.push('colour');
        return { text, families, topics, colours };
      }
      blogRequestExcluded(value) {
        const text = this.blogFeatures(value).text.replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
        return /\b(?:commandes?|orders?|tracking|livraison\w*|deliver\w*|retours?|returns?|refund\w*|rembours\w*|paiement\w*|payment\w*|annul\w*)\b/.test(text) || /^(?:merci|thanks|thank you)\b/.test(text) || /^(?:salut|bonjour|bonsoir|hello|hi|hey|coucou)(?: maya)?(?: comment (?:ca va|vas tu)| how are you)?$/.test(text);
      }
      blogQuestionContext(body, explicitQuestion) {
        const target = body.closest?.('.assistant-message');
        const nodes = this.chatNodes || [];
        const targetNode = nodes.find(node => node.message === target);
        const targetSeq = Number(targetNode?.message?.dataset?.supportSeq);
        let preceding;
        if (targetSeq > 0) preceding = nodes.filter(node => node.role === 'user' && Number(node.message?.dataset?.supportSeq) > 0 && Number(node.message.dataset.supportSeq) < targetSeq).sort((a, b) => Number(a.message.dataset.supportSeq) - Number(b.message.dataset.supportSeq));
        if (!preceding?.length) {
          const positions = new Map(Array.from(this.feed?.children || [], (node, index) => [node, index]));
          const ordered = [...nodes].sort((a, b) => (positions.get(a.message) ?? Infinity) - (positions.get(b.message) ?? Infinity));
          const targetIndex = ordered.findIndex(node => node.message === target);
          preceding = ordered.slice(0, targetIndex >= 0 ? targetIndex : ordered.length).filter(node => node.role === 'user');
        }
        const questions = preceding.map(node => String(node.body?.oleaText ?? node.text ?? ''));
        const question = typeof explicitQuestion === 'string' && explicitQuestion.trim() ? explicitQuestion : questions.at(-1) || '';
        return { question, prior: questions.filter((text, index) => index < questions.length - 1 || text !== question).slice(-3).join(' ') };
      }
      blogArticleLabel(article) {
        const title = String(article?.title || '').trim();
        const compact = Array.from(title).length > 90 ? Array.from(title).slice(0, 88).join('').replace(/\s+\S*$/, '').trim() + '…' : title;
        return (this.strings.blog_recommendation || '💡 __title__').replace('__title__', compact);
      }
      recommendBlogArticle(question, prior = '') {
        if (this.supportState?.mode === 'human' || this.supportState?.mode === 'closed' || this.supportState?.chatBanned || this.blogRequestExcluded(question)) return null;
        const query = this.blogFeatures(question);
        if (!query.families.length && !query.topics.length) return null;
        const familyContext = query.families.length ? query : this.blogFeatures(prior + ' ' + (this.pageContext?.title || ''));
        const page = this.blogFeatures(this.pageContext?.title || '');
        const pageMatchesFamily = !query.families.length || query.families.some(key => page.families.includes(key));
        const colours = query.colours.length ? query.colours : pageMatchesFamily ? page.colours : [];
        const explicitAdvice = /\b(?:conseil\w*|chois\w*|compar\w*|comment|pourquoi|quel\w*|aide\w*|assoc\w*|accord\w*|recommand\w*|help|advice|choos\w*|which|how|what|match\w*)\b/.test(query.text);
        const productLookup = /\b(?:montre\w*|affiche\w*|voir|show|find|trouve\w*|cherche\w*|looking for|je veux|i want)\b/.test(query.text) && !explicitAdvice;
        const adviceTopic = /\b(?:couleurs?|colors?|colours?|teintes?|styles?|ambiances?|decor\w*)\b/.test(query.text) || query.topics.some(key => !['colour', 'style'].includes(key));
        const focusedAdvice = !productLookup && (explicitAdvice || adviceTopic);
        let best = null, bestScore = 0;
        for (const article of (Array.isArray(this.blogContext?.articles) ? this.blogContext.articles : []).slice(0, 50)) {
          if (typeof article?.title !== 'string' || !article.title.trim() || article.title.length > 240 || !this.safeArticleUrl(article.url)) continue;
          const heading = this.blogFeatures(article.title + ' ' + (article.tags || ''));
          const full = this.blogFeatures(heading.text + ' ' + (article.excerpt || ''));
          if (colours.length && heading.colours.length && !colours.some(key => heading.colours.includes(key))) continue;
          if (heading.topics.length && (!focusedAdvice || !query.topics.some(key => heading.topics.includes(key)))) continue;
          const familyMatches = familyContext.families.filter(key => heading.families.includes(key)).length;
          if (familyContext.families.length && !familyMatches) continue;
          const topicMatches = query.topics.filter(key => full.topics.includes(key)).length;
          if (focusedAdvice && query.topics.length && !topicMatches) continue;
          const headingTopics = query.topics.filter(key => heading.topics.includes(key)).length;
          const score = familyMatches * 8 + topicMatches * 2 + headingTopics * 3;
          if (score > bestScore) { best = article; bestScore = score; }
        }
        return best;
      }
      renderActions(body, actions, explicitQuestion) {
        body.querySelectorAll('[data-assistant-navigation]').forEach(row => row.remove());
        const row = document.createElement('div'); row.className = 'assistant-result-actions'; row.dataset.assistantNavigation = '';
        const context = this.blogQuestionContext(body, explicitQuestion);
        const articleAllowed = !this.supportState?.chatBanned && !['human', 'closed'].includes(this.supportState?.mode) && !this.blogRequestExcluded(context.question);
        const seen = new Set(); let hasArticle = false;
        for (const action of (Array.isArray(actions) ? actions : []).slice(0, 3)) {
          if (!['article', 'tracking'].includes(action?.kind)) continue;
          let url;
          try { url = new URL(action.url, location.origin); } catch { continue; }
          if (url.protocol !== 'https:' || url.username || url.password || url.origin !== location.origin) continue;
          if (action.kind === 'article' && (!articleAllowed || hasArticle || !this.safeArticleUrl(action.url))) continue;
          const target = action.kind === 'tracking' ? this.dataset.accountUrl : url.href;
          if (!target || seen.has(target)) continue;
          seen.add(target);
          const link = document.createElement('a'); link.className = 'button button--outline button--small'; link.href = target;
          if (action.kind === 'tracking') link.textContent = this.strings.track_order;
          else {
            hasArticle = true; link.dataset.assistantArticle = '';
            const article = this.blogContext?.articles?.find?.(item => this.safeArticleUrl(item?.url)?.pathname === url.pathname);
            link.textContent = article?.title ? this.blogArticleLabel(article) : this.strings.read_guide;
            if (article?.title) { link.title = article.title; link.setAttribute('aria-label', article.title); }
          }
          row.append(link);
        }
        if (articleAllowed && !hasArticle) {
          const article = this.recommendBlogArticle(context.question, context.prior);
          if (article) {
            const link = document.createElement('a'); link.className = 'button button--outline button--small'; link.dataset.assistantArticle = ''; link.href = this.safeArticleUrl(article.url).href;
            link.textContent = this.blogArticleLabel(article); link.title = article.title; link.setAttribute('aria-label', article.title); row.append(link);
          }
        }
        if (row.childElementCount) body.append(row);
      }
      renderSuggestions(body, suggestions) {
        body.querySelectorAll('.assistant-quick-replies').forEach(row => row.remove());
        const items = [];
        const products = [...body.querySelectorAll('.assistant-product-grid .approved-panel-product')];
        if (products.length) {
          if (products.some(card => /canap|sofa|couch/i.test(card.querySelector('.product-card__title')?.textContent || ''))) items.push(this.strings.quick_four);
          items.push(this.strings.quick_delivery);
          if (products.some(card => Number(card.dataset.panelColorCount) > 1)) items.push(this.strings.quick_shades);
        }
        if (Array.isArray(suggestions)) items.push(...suggestions);
        const row = document.createElement('div'); row.className = 'assistant-result-actions assistant-quick-replies';
        [...new Set(items.filter(value => typeof value === 'string' && value.trim()).map(value => value.trim()))].slice(0, 4).forEach(suggestion => {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'pill'; button.textContent = suggestion.slice(0, 150); button.dataset.assistantPrompt = suggestion.slice(0, 1000); row.append(button);
        });
        if (row.childElementCount) body.append(row);
        this.syncQuickReplies();
      }
      syncQuickReplies() {
        const latest = [...this.feed.querySelectorAll('.assistant-message')].at(-1);
        this.feed.querySelectorAll('.assistant-quick-replies').forEach(replies => {
          replies.hidden = !latest?.classList.contains('assistant-message--assistant') || latest.classList.contains('is-thinking') || latest.classList.contains('is-error') || replies.closest('.assistant-message') !== latest;
        });
      }
      decorateProductCard(card) {
        const product = card.querySelector('.approved-panel-product');
        const body = product?.querySelector('.product-card__body');
        if (!product || !body) return;
        const count = Number(product.dataset.panelColorCount);
        const color = body.querySelector('.approved-panel-product__color');
        if (color && Number.isSafeInteger(count) && count > 0) {
          const original = color.dataset.assistantColor || color.textContent.trim();
          const label = count === 1 ? this.strings.tints_one : this.strings.tints_many;
          if (original && label) { color.dataset.assistantColor = original; color.textContent = original + ' · ' + label.replace('__count__', String(count)); }
        }
        const titleLink = body.querySelector('.product-card__title a[href]');
        if (titleLink && !product.querySelector('.assistant-product-view')) {
          const view = document.createElement('a'); view.className = 'assistant-product-view';
          view.href = titleLink.getAttribute('href'); view.textContent = this.strings.card_view;
          body.append(view);
        }
      }
      async renderProducts(body, result, signal, version = this.version) {
        const current = () => !signal.aborted && version === this.version && this.isConnected && this.dialog?.open;
        if (!current()) return { rendered: 0, failed: 0 };
        const identifiers = Array.isArray(result.comparisonIds) && result.comparisonIds.length ? result.comparisonIds : result.productIds;
        if (!Array.isArray(identifiers)) return { rendered: 0, failed: 0 };
        const grid = document.createElement('div'); grid.className = 'product-grid assistant-product-grid';
        const references = [], handles = new Set(), ids = new Set();
        let invalid = 0;
        for (const id of identifiers) {
          if (ids.has(id)) continue;
          ids.add(id);
          const detail = result.productDetails?.[id];
          const handle = detail?.handle;
          const variantReference = String(detail?.variantId || detail?.selectedVariantId || '');
          const variant = variantReference.replace(/^gid:\/\/shopify\/ProductVariant\//, '');
          if (typeof handle !== 'string' || !/^[a-z0-9][a-z0-9-]{0,199}$/i.test(handle) || (variant && !/^\d{1,20}$/.test(variant))) { invalid++; continue; }
          if (handles.has(handle)) continue;
          handles.add(handle); references.push({ handle, variant });
          if (references.length === 3) break;
        }
        // This state reflects actual storefront requests, never a timed simulation.
        const preparingCurrentReply = this.busy && references.length > 0 && body === this.pending?.content;
        if (preparingCurrentReply) this.setMascotState('searching');
        const settled = await Promise.allSettled(references.map(async ({ handle, variant }) => {
          if (!current()) throw signal.reason || abortError();
          const root = new URL(window.Shopify?.routes?.root || window.Olea?.root || '/', location.origin);
          if (root.origin !== location.origin || root.username || root.password) throw new Error('Invalid storefront root');
          const url = new URL(`${root.pathname.replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}`, location.origin);
          if (variant) url.searchParams.set('variant', variant);
          url.searchParams.set('section_id', 'approved-panel-card');
          const currency = window.Shopify?.currency?.active || window.Olea?.currency;
          if (typeof currency === 'string' && /^[A-Z]{3}$/.test(currency)) url.searchParams.set('currency', currency);
          const theme = window.Shopify?.theme;
          if (theme?.id && ['unpublished', 'development'].includes(theme.role) && /^\d{1,20}$/.test(String(theme.id))) url.searchParams.set('preview_theme_id', String(theme.id));
          const html = await productFragment(url, signal);
          if (!current()) throw signal.reason || abortError();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('[data-wishlist-card]');
          if (!card || card.dataset.handle !== handle || !/^\d{1,20}$/.test(card.dataset.variantId) || (variant && card.dataset.variantId !== variant) || !card.querySelector('.product-card')) throw new Error('Product variant unavailable');
          this.decorateProductCard(card);
          if (variant) card.querySelectorAll('a[href]').forEach(link => { const target = new URL(link.href, location.origin); if (target.origin === location.origin && target.pathname === url.pathname && !target.searchParams.has('option_values')) { target.searchParams.set('variant', variant); link.href = target.pathname + target.search; } });
          return card;
        }));
        if (!current()) return { rendered: 0, failed: 0 };
        settled.forEach(item => { if (item.status === 'fulfilled') grid.append(item.value); });
        if (grid.childElementCount) { body.append(grid); this.renderSuggestions(body, result.suggestions); }
        const failed = invalid + settled.filter(item => item.status === 'rejected').length;
        if (failed) { const note = document.createElement('p'); note.className = 'assistant-commerce-note'; note.textContent = this.strings.cards_error; body.append(note); }
        if (preparingCurrentReply) this.setMascotState('writing');
        return { rendered: grid.childElementCount, failed };
      }
    });
  }
  // External integrations use the same gate; navigating to contact/WhatsApp stays immediate.
  const externalAssistant = {
    opener: null, anchor: null,
    get isConnected() { return this.anchor?.isConnected === true; },
    contains(target) { return this.anchor?.contains(target) || false; },
    close() {
      window.OleaHeaderPanels?.cancel(this);
      window.OleaHeaderPanels?.release(this);
      this.opener?.setAttribute('aria-expanded', 'false');
    }
  };
  document.addEventListener('olea:header-panel-open', event => { if (event.detail.source !== externalAssistant) externalAssistant.close(); });
  document.addEventListener('olea:header-panel-close', () => externalAssistant.close());
  matchMedia('(min-width:990px)').addEventListener('change', () => externalAssistant.close());
  document.addEventListener('olea:assistant-warmup', () => {
    const anchor = document.querySelector('assistant-launcher');
    if (anchor?.dataset.endpoint && !anchor.dataset.selector && !anchor.dataset.event) void document.querySelector('olea-assistant')?.prepare(anchor.dataset.endpoint);
  });
  document.addEventListener('click', async event => {
    const trigger = event.target.closest('[data-assistant-open]');
    if (!trigger) return;
    const anchor = document.querySelector('assistant-launcher');
    if (!anchor) return;
    const query = trigger.dataset.assistantQuery;
    const autoSend = trigger.dataset.assistantAutosend === 'true';
    const dialogClosed = window.OleaOverlayMotion.hide(trigger.closest('dialog'));
    let selectorButton;
    if (anchor.dataset.selector) {
      try { selectorButton = document.querySelector(anchor.dataset.selector); } catch {}
    }
    if (selectorButton && (selectorButton === trigger || selectorButton.matches('[data-assistant-open]'))) selectorButton = null;
    if (selectorButton || anchor.dataset.event) {
      event.preventDefault();
      await dialogClosed;
      externalAssistant.opener = trigger;
      externalAssistant.anchor = anchor;
      const selector = selectorButton ? anchor.dataset.selector : null;
      const eventName = anchor.dataset.event;
      const reveal = () => {
        if (!anchor.isConnected) return false;
        if (selector) {
          const button = document.querySelector(selector);
          if (!button || button === trigger || button.matches('[data-assistant-open]')) { externalAssistant.close(); return false; }
          button.click();
        } else document.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { source: 'olea', page: location.href, title: document.title } }));
        trigger.setAttribute('aria-expanded', 'true');
        return true;
      };
      try { await (window.OleaHeaderPanels ? window.OleaHeaderPanels.open('assistant', externalAssistant, reveal) : Promise.resolve(reveal())); }
      catch { externalAssistant.close(); window.Olea?.toast(anchor.dataset.error); }
      return;
    }
    if (anchor.dataset.endpoint && window.OleaAssistantTransport) {
      try {
        event.preventDefault();
        await dialogClosed;
        await document.querySelector('olea-assistant')?.open(anchor.dataset.endpoint, trigger, query, autoSend);
        return;
      } catch { window.Olea?.toast(anchor.dataset.error); }
    }
    const contact = anchor.dataset.contact || '';
    if (contact.startsWith('https://wa.me/')) window.open(contact, '_blank', 'noopener');
    else if (contact) location.href = contact;
  });
})();





